import { openDB } from 'idb';

const DB_NAME = 'pb-db';
const TABLE_NAME = 'kvStore';
const DB_VERSION = 2;

// Mirrors LOCAL_STORAGE_KEYS.RECORD_BINDERS / RECORD_HISTORY / META_DATA and
// MODULE_NAMES.RECORD_BINDER_MODULE from constants/AppConstants.js. Inlined
// (not imported) to avoid a circular dependency - AppConstants.js already
// imports getLocale from this file. Keep these in sync if those change.
const RECORD_BINDERS_KEY = 'recordBinders';
const RECORD_HISTORY_KEY = 'recordHistory';
const META_DATA_KEY = 'metadata';
const RECORD_BINDER_MODULE = 'RecordBinderModule';

const DB = async () =>
  await openDB(DB_NAME, DB_VERSION, {
    async upgrade(db, oldVersion, newVersion, transaction) {
      if (oldVersion < 1) {
        db.createObjectStore(TABLE_NAME);
      }

      if (oldVersion < 2) {
        const store = transaction.objectStore(TABLE_NAME);
        await store.delete(RECORD_BINDERS_KEY);
        await store.delete(RECORD_HISTORY_KEY);

        const metaDataList = await store.get(META_DATA_KEY);
        if (Array.isArray(metaDataList)) {
          const filtered = metaDataList.filter(
            (entry) => entry.moduleName !== RECORD_BINDER_MODULE,
          );
          await store.put(filtered, META_DATA_KEY);
        }
      }
    },
    blocked() {
      // Another tab still has an older-version connection open; this
      // connection can't proceed until that tab closes/reloads.
      console.log('PBStorage upgrade blocked by another open tab');
    },
    blocking() {
      // This connection is now stale because another tab requested a
      // newer version; PBStorage doesn't cache a persistent connection
      // (a fresh one is opened per call), so no explicit close is needed.
      console.log('PBStorage: newer version requested by another tab');
    },
  });

export const PBStorage = {
  get: async (key) => (await DB()).get(TABLE_NAME, key),
  set: async (key, value) => (await DB()).put(TABLE_NAME, value, key),
  del: async (key) => (await DB()).delete(TABLE_NAME, key),
  clear: async () => (await DB()).clear(TABLE_NAME),
  keys: async () => (await DB()).getAllKeys(TABLE_NAME),
};

let _token = null,
  _username = '',
  _userRole = null,
  _userId = null,
  _locale = 'en',
  _lastSyncTime = null,
  _isFirstLogin = true,
  _selectedFeedlotName = '',
  _selectedFeedlotTrainingRulesEnabled = 0,
  _selectedFeedlotRankEnabled = 0,
  _selectedFeedlotId = null,
  _userFirstName = '',
  _userLastName = '',
  _isSessionExpired = false;

export const setToken = (token) => {
  _token = token;
};

export const getToken = () => {
  return _token;
};

export const setUsername = (username) => {
  _username = username;
};

export const getUsername = () => {
  return _username;
};

export const setUserRole = (userRole) => {
  _userRole = userRole;
};

export const getUserRole = () => {
  return _userRole;
};

export const setUserId = (userId) => {
  _userId = userId;
};

export const getUserId = () => {
  return _userId;
};

export const setLocale = (locale) => {
  _locale = locale;
  localStorage.setItem('language', locale);
};

export const getLocale = () => {
  return localStorage.getItem('language') || 'en';
};

export const setLastSyncTime = (lastSyncTime) => {
  _lastSyncTime = lastSyncTime;
};

export const getLastSyncTime = () => {
  return _lastSyncTime;
};

export const setFirstLogin = (isFirstLogin) => {
  _isFirstLogin = isFirstLogin;
};

export const getFirstLogin = () => {
  return _isFirstLogin;
};

export const setSelectedFeedlotName = (selectedFeedlotName) => {
  _selectedFeedlotName = selectedFeedlotName;
};

export const getSelectedFeedlotName = () => {
  return _selectedFeedlotName;
};

export const setSelectedFeedlotId = (selectedFeedlotId) => {
  _selectedFeedlotId = selectedFeedlotId;
};

export const getSelectedFeedlotId = () => {
  if (_selectedFeedlotId) {
    return _selectedFeedlotId;
  }

  try {
    return localStorage.getItem('selectedFeedyard') || null;
  } catch (e) {
    return null;
  }
};

export const setSelectedFeedlotTrainingRulesEnabled = (
  selectedFeedlotTrainingRulesEnabled,
) => {
  _selectedFeedlotTrainingRulesEnabled = selectedFeedlotTrainingRulesEnabled;
};

export const getSelectedFeedlotTrainingRulesEnabled = () => {
  return _selectedFeedlotRankEnabled;
};

export const setSelectedFeedlotRankEnabled = (selectedFeedlotRankEnabled) => {
  _selectedFeedlotRankEnabled = selectedFeedlotRankEnabled;
};

export const getSelectedFeedlotRankEnabled = () => {
  return _selectedFeedlotRankEnabled;
};

export const setUserFirstName = (firstName) => {
  _userFirstName = firstName;
};

export const getUserFirstName = () => {
  return _userFirstName;
};

export const setUserLastName = (lastName) => {
  _userLastName = lastName;
};

export const getUserLastName = () => {
  return _userLastName;
};

export const setSessionExpired = (isSessionExpired) => {
  _isSessionExpired = isSessionExpired;
};

export const getSessionExpired = () => {
  return _isSessionExpired;
};
